import React from "react";
import {
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Card,
  Switch,
  TextField,
  Chip,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

/* ---------------- STEPS ---------------- */
const steps = [
  { label: "Validity Period", sub: "Financial year" },
  { label: "Calculation Rules", sub: "Define slabs" },
  { label: "Eligibility", sub: "Optional constraints" },
  { label: "CP Sign-Off", sub: "Configure window" },
  { label: "Exclusions", sub: "POP projects" },
  { label: "Review", sub: "Preview & save" },
];

export default function AOPCPSignOff({
  data,
  setData,
  onBack,
  onNext,
}) {
  const {
    signOffRequired = false,
    signOffStartDate = "",
    signOffEndDate = "",
  } = data;

  const updateField = (key, value) => {
    setData({
      ...data,
      [key]: value,
    });
  };

  return (
    <Box sx={{ p: 1, minHeight: "100vh" }}>
 

{/* Back */}
<Typography
  variant="body2"
  sx={{
    mb: 3,
    cursor: "pointer",
    color: "text.secondary",
    fontSize: 14,
    fontWeight: 500,
  }}
  onClick={onBack}
>
  ← Back to AOPs
</Typography>

{/* Stepper */}
<Stepper
  activeStep={3}
  alternativeLabel
  sx={{
    mb: 6,
    "& .MuiStepLabel-label": {
      fontSize: "0.95rem",
      fontWeight: 500,
    },
    "& .MuiStepIcon-root": {
      fontSize: 32,
    },
    "& .Mui-active": {
      color: "#2563EB",
    },
    "& .Mui-completed": {
      color: "#2563EB",
    },
  }}
>
  {steps.map((step) => (
    <Step key={step.label}>
      <StepLabel>
        <Box textAlign="center">
          <Typography
            fontSize="0.95rem"
            fontWeight={600}
          >
            {step.label}
          </Typography>
          <Typography
            fontSize="0.75rem"
            color="text.secondary"
          >
            {step.sub}
          </Typography>
        </Box>
      </StepLabel>
    </Step>
  ))}
</Stepper>


      {/* Info */}
      <Box
        sx={{
          background: "#EFF6FF",
          border: "1px solid #BFDBFE",
          borderRadius: 2,
          p: 2,
          display: "flex",
          gap: 1.5,
          mb: 3,
        }}
      >
        <InfoOutlinedIcon sx={{ color: "#2563EB" }} />
        <Typography fontSize={13} color="#1E3A8A">
          <b>AOP Achievement Logic</b>
          <br />
          CPs must accept the AOP within the configured window to be eligible.
        </Typography>
      </Box>

      {/* CP Sign-Off Card */}
      <Card
        sx={{
          p: 3,
          borderRadius: 2,
          border: "1px solid #E5E7EB",
          boxShadow: "none",
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <PersonOutlineIcon color="primary" />
          <Typography fontSize={16} fontWeight={600}>
            CP Sign-Off Configuration
          </Typography>
        </Box>

        {/* Mandatory Toggle */}
        <Box
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: 2,
            p: 2,
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography fontSize={14} fontWeight={500}>
              Mandatory Sign-Off
            </Typography>
            <Typography fontSize={12} color="#6B7280">
              CPs must sign AOP to participate
            </Typography>
          </Box>
          <Switch
            checked={signOffRequired}
            onChange={(e) =>
              updateField("signOffRequired", e.target.checked)
            }
          />
        </Box>

        {/* Dates */}
        {signOffRequired && (
          <Box display="flex" gap={2} mb={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Sign-Off Start Date"
              InputLabelProps={{ shrink: true }}
              value={signOffStartDate}
              onChange={(e) =>
                updateField("signOffStartDate", e.target.value)
              }
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Sign-Off End Date"
              InputLabelProps={{ shrink: true }}
              value={signOffEndDate}
              onChange={(e) =>
                updateField("signOffEndDate", e.target.value)
              }
            />
          </Box>
        )}

        {/* Warning */}
        <Box
          sx={{
            background: "#FFF7ED",
            border: "1px solid #FED7AA",
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
        >
          <Typography fontSize={12} color="#9A3412">
            After the sign-off end date, CP sign-off will be automatically
            disabled.
          </Typography>
        </Box>

        {/* Dashboard (static preview) */}
        {/* <Typography fontSize={14} fontWeight={600} mb={1}>
          CP Sign-Off Dashboard
        </Typography>
        <Box display="flex" gap={2}>
          <StatCard value="45" label="Total CPs" bg="#F1F5F9" />
          <StatCard value="32" label="Signed" bg="#ECFDF5" color="#16A34A" />
          <StatCard value="13" label="Pending" bg="#FFF7ED" color="#F97316" />
          <StatCard value="Active" label="Window Open" bg="#EEF2FF" chip />
        </Box> */}
      </Card>

      {/* Footer */}
      <Box display="flex" justifyContent="space-between" mt={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back
        </Button>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={onNext}
          disabled={
            signOffRequired &&
            (!signOffStartDate || !signOffEndDate)
          }
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}

/* ---------------- STAT CARD ---------------- */

function StatCard({ value, label, bg, color, chip }) {
  return (
    <Box
      sx={{
        flex: 1,
        background: bg,
        borderRadius: 10,
        p: 2,
        textAlign: "center",
      }}
    >
      {chip ? (
        <Chip label={value} color="success" size="small" />
      ) : (
        <Typography fontSize={20} fontWeight={700} color={color}>
          {value}
        </Typography>
      )}
      <Typography fontSize={12} color="#6B7280">
        {label}
      </Typography>
    </Box>
  );
}
